import { Loader2 } from 'lucide-react'
import { Alert, AlertDescription, Button } from '@atrvd/saas-ui'

interface QueryErrorProps {
  /** Raw message of the failed query (ADR 0006). */
  message: string
  /** Refetch the failed query. */
  onRetry: () => void
  /**
   * A refetch is in flight. With data on screen TanStack keeps `error` set
   * until it settles, so without this a click on Retry shows no reaction.
   */
  retrying: boolean
}

/**
 * Retry that turns into a disabled "Retrying…" while the refetch runs, so a
 * second click cannot stack another request on the one in flight.
 */
function RetryButton({ onRetry, retrying }: Pick<QueryErrorProps, 'onRetry' | 'retrying'>) {
  return (
    <Button variant="outline" size="sm" onClick={onRetry} disabled={retrying} className="gap-2">
      {retrying && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
      {retrying ? 'Retrying…' : 'Retry'}
    </Button>
  )
}

/**
 * Failed refresh of a table whose rows are still on screen, shown above the
 * table (see `queryErrorDisplay`). `role="alert"` comes from the saas-ui `Alert`.
 * No outer margin: the parent's `space-y-*` / `gap-*` spaces it.
 */
export function QueryErrorBanner({ message, onRetry, retrying }: QueryErrorProps) {
  return (
    <Alert variant="destructive" className="flex items-center justify-between gap-3 py-2">
      <AlertDescription>{message}</AlertDescription>
      <RetryButton onRetry={onRetry} retrying={retrying} />
    </Alert>
  )
}

/**
 * Message + Retry for a DataTable `error` slot. No role of its own: DataTable
 * already wraps the slot in `role="alert"`.
 */
export function QueryErrorMessage({ message, onRetry, retrying }: QueryErrorProps) {
  return (
    <div className="flex flex-col items-center gap-3">
      <p>{message}</p>
      <RetryButton onRetry={onRetry} retrying={retrying} />
    </div>
  )
}
